
import React, { memo } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAuthGuard } from '@/hooks/useAuthGuard';
import { usePortalStatus } from '@/hooks/usePortalStatus';
import { authLogger } from '@/utils/debug';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Button } from '@/components/ui/button';

type UserRole = 'client' | 'staff' | 'admin' | 'super_admin';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
  redirectTo?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = memo(({
  children,
  allowedRoles = [],
  redirectTo = '/auth'
}) => {
  const { user, profile, loading } = useAuth();
  const { isPortalActive, isLoading: portalLoading, error: portalError } = usePortalStatus();

  authLogger.log('🔥 ProtectedRoute render - User:', user?.id, 'Profile:', profile?.id, 'Role:', profile?.role, 'Loading:', loading);

  // Use the auth guard to continuously validate user access (but much less aggressively now)
  useAuthGuard();

  if (loading || portalLoading) {
    authLogger.log('🔥 ProtectedRoute: Still loading...');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user || !profile) {
    authLogger.log('🔥 ProtectedRoute: No user or profile, redirecting to:', redirectTo);
    return <Navigate to={redirectTo} replace />;
  }

  // Check if profile is active
  if (!profile.is_active) {
    authLogger.log('🔥 ProtectedRoute: Profile inactive, redirecting to auth');
    return <Navigate to="/auth" replace />;
  }

  // Check portal maintenance mode for non-admin and non-super-admin users
  // If there's an error fetching portal status, assume portal is active to avoid blocking users
  if (!portalError && isPortalActive === false && profile.role !== 'admin' && profile.role !== 'super_admin') {
    authLogger.log('🔥 ProtectedRoute: Portal in maintenance mode, redirecting non-admin user');
    return <Navigate to="/maintenance" replace />;
  }

  if (allowedRoles.length > 0) {
    // Super admins can access everything, including admin routes
    const isSuperAdmin = profile.role === 'super_admin';
    const hasDirectAccess = allowedRoles.includes(profile.role);

    // Allow access if user has direct role access OR if they're super admin
    const hasAccess = hasDirectAccess || isSuperAdmin;

    authLogger.log('🔥 ProtectedRoute: Role check -', {
      userRole: profile.role,
      allowedRoles,
      isSuperAdmin,
      hasDirectAccess,
      hasAccess
    });

    if (!hasAccess) {
      authLogger.log('🔥 ProtectedRoute: Role not allowed, redirecting to unauthorized');
      return <Navigate to="/unauthorized" replace />;
    }
  }

  authLogger.log('🔥 ProtectedRoute: Access granted, rendering children');

  return (
    <ErrorBoundary
      fallback={
        <div className="flex flex-col items-center justify-center min-h-[50vh] p-4 text-center">
          <div className="p-4 bg-red-50 rounded-full mb-4">
            <svg
              className="w-12 h-12 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Error</h2>
          <p className="text-gray-600 mb-6 max-w-md">
            We encountered an unexpected error while loading this protected route.
            Please try again or return to the dashboard.
          </p>
          <div className="flex gap-4">
            <Button
              onClick={() => window.location.reload()}
              variant="outline"
            >
              Reload Page
            </Button>
            <Button
              onClick={() => window.location.href = '/'}
              variant="default"
            >
              Return to Dashboard
            </Button>
          </div>
        </div>
      }
    >
      {children}
    </ErrorBoundary>
  );
});

ProtectedRoute.displayName = 'ProtectedRoute';

export default ProtectedRoute;
